import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/Button';
import { useToast } from '../components/ui/Toast';
import { useApi } from '../hooks/useApi';
import { StatusBadge } from './ui/StatusBadge';
import { EmptyState } from './ui/EmptyState';
import { Markdown } from './ui/Markdown';
import type { ReviewRun, FindingSeverity } from '@pantheon/shared';

const reviewStatus: Record<string, { text: string; color: string }> = {
  pending: { text: 'Queued', color: 'bg-gray-100 text-gray-600' },
  running: { text: 'Reviewing', color: 'bg-blue-50 text-blue-600' },
  reporting: { text: 'Reporting', color: 'bg-blue-50 text-blue-600' },
  planned: { text: 'Fix plan created', color: 'bg-amber-50 text-amber-700' },
  passed: { text: 'Review passed', color: 'bg-success-light text-success' },
  declined: { text: 'Declined', color: 'bg-gray-100 text-gray-500' },
  failed: { text: 'Run failed', color: 'bg-danger-light text-danger' },
};

const sevColor: Record<FindingSeverity, string> = {
  blocker: 'bg-red-100 text-red-700',
  issue: 'bg-amber-100 text-amber-700',
  nit: 'bg-gray-100 text-gray-500',
};

/**
 * Argus — the self-correcting review loop, on the initiative page. Runs a review
 * (boot the app, check the acceptance criteria, axe, build/lint/test), lists the
 * runs, and surfaces the findings + the fix-plan it authored.
 */
export function ArgusReviewPanel({
  initiativeId,
  canReview,
  loopOutcome,
}: {
  initiativeId: string;
  canReview: boolean;
  loopOutcome?: 'in_progress' | 'passed' | 'declined' | 'capped';
}) {
  const { get, post } = useApi();
  const { toast } = useToast();
  const [runs, setRuns] = useState<ReviewRun[]>([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const getRef = useRef(get);
  getRef.current = get;

  const fetchRuns = useCallback(async () => {
    const result = await getRef.current<ReviewRun[]>(`/argus/initiatives/${initiativeId}/reviews`);
    if (result.success && result.data) setRuns(result.data);
  }, [initiativeId]);

  useEffect(() => {
    fetchRuns();
    const interval = setInterval(fetchRuns, 10_000); // reviews take minutes; poll while the panel is open
    return () => clearInterval(interval);
  }, [fetchRuns]);

  const runReview = async () => {
    setBusy(true);
    const result = await post<ReviewRun>(`/argus/initiatives/${initiativeId}/review`, {});
    setBusy(false);
    if (result.success) {
      toast('Argus is booting the app and reviewing it…', 'info');
      fetchRuns();
    } else {
      toast(result.error?.message ?? 'Failed to start review', 'error');
    }
  };

  const decline = async (reviewRunId: string) => {
    const result = await post(`/argus/reviews/${reviewRunId}/decline`, {});
    if (result.success) {
      toast('Fix plan declined — the review loop is stopped.', 'info');
      fetchRuns();
    } else {
      toast(result.error?.message ?? 'Failed to decline', 'error');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Self-Correcting Review — Argus</h3>
        {canReview && (
          <Button size="sm" onClick={runReview} loading={busy}>
            Run review
          </Button>
        )}
      </div>

      {!canReview && (
        <p className="text-xs text-gray-500 mb-2">
          Available once the initiative is fanned out and has an integration branch.
        </p>
      )}

      {loopOutcome === 'capped' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800 mb-2">
          Argus hit the maximum review iterations without a clean pass. A human should take a look before continuing.
        </div>
      )}

      {runs.length === 0 ? (
        <EmptyState
          title="No reviews yet"
          hint="Argus boots the app, walks it against the acceptance criteria, runs an accessibility audit and the tests, and turns anything it finds into a fix plan."
        />
      ) : (
        <div className="space-y-2">
          {runs.map((run) => {
            const st = reviewStatus[run.status] ?? reviewStatus.pending;
            const findings = run.findings ?? [];
            const blockers = findings.filter((f) => f.severity === 'blocker').length;
            const issues = findings.filter((f) => f.severity === 'issue').length;
            const nits = findings.filter((f) => f.severity === 'nit').length;
            const acPass = (run.acResults ?? []).filter((a) => a.pass).length;
            const acTotal = (run.acResults ?? []).length;
            const isOpen = expanded === run.reviewRunId;
            return (
              <div key={run.reviewRunId} className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpanded(isOpen ? null : run.reviewRunId)}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-gray-500">#{run.iteration}</span>
                    <StatusBadge status={run.status} label={st.text} />
                    {acTotal > 0 && (
                      <span className="text-xs text-gray-500">
                        {acPass}/{acTotal} criteria pass
                      </span>
                    )}
                    {blockers + issues > 0 && (
                      <span className="text-xs text-gray-500">
                        {blockers} blocker{blockers === 1 ? '' : 's'} · {issues} issue{issues === 1 ? '' : 's'}
                        {nits ? ` · ${nits} nit${nits === 1 ? '' : 's'}` : ''}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{new Date(run.createdAt).toLocaleString()}</span>
                </div>

                {run.walkthroughFailed && (
                  <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <span className="shrink-0">⚠</span>
                    <span>
                      <b>Walkthrough capture failed</b> — the recorded video/screenshots are unreliable, so this
                      verdict rests on the model&rsquo;s non-visual checks and the loop was <b>not</b> closed. Fix the
                      walkthrough spec and re-run for visual confirmation.
                    </span>
                  </div>
                )}

                {run.note && <Markdown className="mt-1.5">{run.note}</Markdown>}

                {run.steps && run.steps.length > 0 && (run.status === 'running' || run.status === 'pending' || isOpen) && (
                  <ul className="mt-2 space-y-1">
                    {run.steps.map((step) => (
                      <li key={step.key} className="flex items-start gap-2 text-xs">
                        <span
                          className={
                            step.status === 'done'
                              ? 'text-green-600'
                              : step.status === 'running'
                                ? 'text-blue-600'
                                : step.status === 'failed'
                                  ? 'text-red-600'
                                  : 'text-gray-400'
                          }
                        >
                          {step.status === 'done'
                            ? '✓'
                            : step.status === 'running'
                              ? '⏳'
                              : step.status === 'failed'
                                ? '✗'
                                : step.status === 'skipped'
                                  ? '–'
                                  : '○'}
                        </span>
                        <span className={step.status === 'pending' ? 'text-gray-500' : 'text-gray-700'}>
                          {step.label}
                          {(step.status === 'running' || step.status === 'failed') && step.detail && (
                            <span className={`font-mono ${step.status === 'failed' ? 'text-red-500' : 'text-gray-500'}`}> — {step.detail}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {run.videoUrl && (
                  <a
                    className="text-xs text-primary underline mt-1.5 inline-block"
                    href={run.videoUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    ▶ Workflow video
                  </a>
                )}

                {run.screenshots && run.screenshots.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                    <span className="text-xs text-gray-500">Screenshots:</span>
                    {run.screenshots.map((url, i) => (
                      <a
                        key={i}
                        className="text-xs text-primary underline"
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {i + 1}
                      </a>
                    ))}
                  </div>
                )}

                {run.status === 'planned' && run.fixHermesSessionId && (() => {
                  const actionable = findings.filter((f) => f.severity === 'blocker' || f.severity === 'issue');
                  const shown = actionable.slice(0, 6);
                  return (
                    <div className="mt-1.5 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs text-amber-700">
                          A fix plan was authored and added to the plan above as a workstream. Approve it on the{' '}
                          <a className="underline font-medium" href="/hermes">
                            Hermes
                          </a>{' '}
                          page and queue it on Daedalus — merging it kicks off the next review automatically.
                        </p>
                        {actionable.length > 0 && (
                          <>
                            <p className="text-xs text-gray-500 mt-1.5">
                              Fixing {blockers} blocker{blockers === 1 ? '' : 's'}, {issues} issue{issues === 1 ? '' : 's'}:
                            </p>
                            <ul className="list-disc pl-4 mt-1 space-y-0.5 text-xs text-gray-700">
                              {shown.map((f) => (
                                <li key={f.id}>
                                  <span className={`font-semibold px-2 py-px rounded-full mr-1 ${sevColor[f.severity]}`}>
                                    {f.severity}
                                  </span>
                                  {f.title}
                                </li>
                              ))}
                              {actionable.length > shown.length && (
                                <li className="list-none text-gray-500">+{actionable.length - shown.length} more</li>
                              )}
                            </ul>
                          </>
                        )}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => decline(run.reviewRunId)}>
                        Decline &amp; stop
                      </Button>
                    </div>
                  );
                })()}
                {run.status === 'passed' && (
                  <p className="text-xs mt-1.5 text-success">Nothing actionable — the feature is ready for release.</p>
                )}
                {run.status === 'declined' && (
                  <p className="text-xs mt-1.5 text-gray-500">Declined — the loop is stopped. Run a review to restart it.</p>
                )}

                {isOpen && findings.length > 0 && (
                  <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                    {findings.map((f) => (
                      <div key={f.id} className="text-xs">
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold px-2.5 py-0.5 rounded-full ${sevColor[f.severity]}`}>
                            {f.severity}
                          </span>
                          <span className="font-medium text-gray-800">{f.title}</span>
                          {f.ticketKey && <span className="text-gray-500">({f.ticketKey})</span>}
                          <span className="text-gray-400 ml-auto">{f.source}</span>
                        </div>
                        {f.detail && <Markdown className="mt-0.5 ml-1">{f.detail}</Markdown>}
                        {f.screenshot && (
                          <a
                            className="text-primary underline mt-0.5 ml-1 inline-block"
                            href={f.screenshot}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📷 screenshot
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
